import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import {ContextMenuOption} from 'components/ContextMenu';
import {
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {isColumnOutdated} from 'notebook/structured-tables/columns/ColumnDefinitions';
import columnDefinitionToIcon from 'notebook/structured-tables/columns/columnDefinitionToIcon';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {
  StructuredTableColumn,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import TableAxisCell, {DIRECTION} from 'notebook/tables/core-ui/TableAxisCell';
import {getTableCellAffixedProps, allowDeleteColumn} from 'notebook/tables/core-ui/TableCellRenderUtils';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import UnitsTableAxisCell from 'notebook/tables/core-ui/UnitsTableAxisCell';
import {
  Cell,
  ColumnConstraint,
  InsertableColumnInfo,
  SelectionRange,
  Table,
  TABLE_COLUMN,
  TableColumnType,
  TableDimension,
  TableIndex,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

export interface TableHeaderRowProps
  // Pick off of TableRow props to decrease repetition
  extends
    Pick<
      TableRowProps,
      'sliceStart' | 'affixedColumnCount' | 'firstColumnWidth' | 'showAffixedShadow' | 'columnWidths'
    >,
    MediocreSearchResultProps {
  // File info
  fileId: string;
  isConfig: boolean;

  // Table info
  table: Table;
  canAddRemoveColumns: boolean;
  ghostCellsCount?: number;

  // Selection state
  isTableSelected: boolean;
  maxJ: number | null;
  minJ: number | null;
  // Which columns are fully selected (selection spans all rows). Undefined when
  // no full-column selection exists. This triggers re-renders of column header
  // TableAxisCells when the selection transitions to/from a full-column state,
  // which is necessary for the column header context menu to work correctly.
  selectedColumnRange: TableIndex | undefined;

  // Rendering slice (see TableRow.js)
  sliceEnd: number | null;
  rowHeight?: number | null;

  // Rendering config
  locked: boolean;
  viewer: boolean;
  disableLegacyHeader: boolean;
  isLeftAlignColumnHeader: boolean;
  isLeftAlignRowHeader?: boolean;
  showExcessLabels: boolean;
  disableColumnLocking?: boolean;
  isProtected: boolean;
  hideColumnHeaderIcons?: boolean;
  stickyHeaderCells?: boolean; // applies position: sticky to the header cells
  stickyTop?: number; // an offset applied to the sticky header cells

  // Context menu helpers
  getDisallowedColumnNames?: () => Array<string>;
  getExtraContextMenuOptions?: (selectionRange: SelectionRange | null) => Array<ContextMenuOption> | null;
  getSelectionRange: (dim: TableDimension, j: number) => SelectionRange | null;
  insertableColumnInfos?: Array<InsertableColumnInfo>;

  // Table actions
  axisCellDragOverHandler?: (index: TableIndex, e: ElementMouseEvent) => void;
  axisCellDragStartHandler?: (options: {start: TableIndex; active: string}, e: ElementMouseEvent) => void;
  showDragHandle?: boolean;
  axisCellDragHandleMouseDown?: (dim: TableDimension, index: number) => boolean;
  deleteColumns?: (minIndex: number | null, maxIndex: number | null) => void;
  deleteTable?: () => void;
  finalizeAndExitEditMode?: () => void;
  insertColumnsLeft?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo
  ) => void;
  insertColumnsRight?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo
  ) => void;
  renameColumn?: (i: number, name: string) => void;
  setColumnConstraint?: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell: Partial<Cell>
  ) => void;
  freezeThroughColumn?: (j: number) => void;
  unfreezeFromColumn?: (j: number) => void;
  toggleHeaderColHandler?: () => void;

  hasAnyFrozenColumns?: boolean;
}

/**
 * The header row in a TableEditable, containing the maximize icon and Excel column labels (A, B, C, ...)
 */
function TableHeaderRow({
  canAddRemoveColumns = true,
  hideColumnHeaderIcons = false,
  stickyHeaderCells = false,
  sliceStart,
  affixedColumnCount,
  hasAnyFrozenColumns,
  firstColumnWidth,
  showAffixedShadow,
  columnWidths,
  searchResultsTree,
  selectedSearchResultTree,
  fileId,
  isConfig,
  table,
  ghostCellsCount,
  isTableSelected,
  maxJ,
  minJ,
  sliceEnd,
  rowHeight,
  locked,
  viewer,
  disableLegacyHeader,
  isLeftAlignColumnHeader,
  isLeftAlignRowHeader,
  showExcessLabels,
  disableColumnLocking,
  isProtected,
  stickyTop,
  getDisallowedColumnNames,
  getExtraContextMenuOptions,
  getSelectionRange,
  insertableColumnInfos,
  axisCellDragOverHandler,
  axisCellDragStartHandler,
  showDragHandle,
  axisCellDragHandleMouseDown,
  deleteColumns,
  deleteTable,
  finalizeAndExitEditMode,
  insertColumnsLeft,
  insertColumnsRight,
  renameColumn,
  setColumnConstraint,
  freezeThroughColumn,
  unfreezeFromColumn,
  toggleHeaderColHandler,
}: TableHeaderRowProps): JSX.Element | null {
  function canModifyCols(): boolean {
    return !viewer && !locked && TableUtil.allowColumnEdits(table);
  }

  function canInsertCols(j: number, direction: DIRECTION): boolean {
    return canInsertColumns({
      table,
      canAddRemoveColumns,
      insertableColumnInfos,
      getSelectionRange,
      columnIndex: j,
      direction,
    });
  }

  function _columnIsOutdated(table: Table, j: number): boolean {
    const columnDefinition = TableUtil.getColumnDefinition(table, j);
    return columnDefinition == null ? false : isColumnOutdated(columnDefinition);
  }

  function renderCell(j: number, shouldHighlightCustomColumns: boolean): JSX.Element {
    const w = table.table[0].length;
    const isGhostCell = j >= w;
    const editable = canModifyCols() && j < w;
    const columnConstraint = table.columnConstraints[j];
    const isEditableLookupColumn =
      !editable &&
      j < w &&
      !viewer &&
      !locked &&
      Boolean(table.allowLookupColumnEdits) &&
      columnConstraint?.columnDefinition?.type === TableColumnType.DEDICATED_LOOKUP;
    const CellComponent = getCellComponent(table, j);
    const tableCellAffixedProps = getTableCellAffixedProps({
      j,
      sliceStart,
      affixedColumnCount,
      hasAnyFrozenColumns,
      firstColumnWidth,
      showAffixedShadow,
      columnWidths,
    });
    const shouldHighlightCell =
      shouldHighlightCustomColumns &&
      columnConstraint?.columnDefinition?.type === TableColumnType.CUSTOM &&
      // Custom Columns should not be highlighted if they have an assigned column group (indicating they have
      // a specific role and were not just added by the user)
      columnConstraint?.columnDefinition?.columnGroup == null &&
      // Parameter tables use custom cols for datetime values, but these should not be highlighted
      TableUtil.getTableKind(table) !== StructuredTableKind.PARAMETER;

    return (
      <CellComponent
        key={`axis-${j}`}
        disableColumnLocking={disableColumnLocking}
        showExcessLabels={showExcessLabels}
        customLabel={TableUtil.getRenderedColumnLabel(table, j)}
        shouldHighlightCell={shouldHighlightCell}
        label={TableUtil.getColumnLabel(table, j)}
        index={j}
        dim={TABLE_COLUMN}
        {...(editable && {
          delDim: deleteColumns,
          renameDim: renameColumn,
          insertDimBefore: insertColumnsLeft,
          insertDimAfter: insertColumnsRight,
        })}
        {...(isEditableLookupColumn && {
          delDim: deleteColumns,
        })}
        {...(editable && j === 0 && !disableLegacyHeader && {toggleHeader: toggleHeaderColHandler})}
        getSelectionRange={getSelectionRange}
        numDims={w}
        deleteTable={deleteTable}
        isTableSelected={isTableSelected}
        {...(!isGhostCell && {
          cellDragStart: axisCellDragStartHandler,
          cellDragOver: axisCellDragOverHandler,
          showDragHandle,
          dragHandleMouseDown: axisCellDragHandleMouseDown,
        })}
        isGhostCell={isGhostCell}
        isSelected={minJ != null && maxJ != null && minJ <= j && j <= maxJ}
        locked={locked}
        // Column information
        setColumnConstraint={setColumnConstraint}
        {...(columnConstraint != null && {columnConstraint})}
        insertableColumnInfos={insertableColumnInfos}
        getExtraContextMenuOptions={getExtraContextMenuOptions}
        getDisallowedColumnNames={getDisallowedColumnNames}
        disallowCustomColumns={TableUtil.disallowCustomColumns(table)}
        shouldDisableInsertDimBefore={!canInsertCols(j, DIRECTION.BEFORE)}
        shouldDisableInsertDimAfter={!canInsertCols(j, DIRECTION.AFTER)}
        disableInsertDimTooltip={table._disableInsertColsTooltip}
        shouldDisableDeleteDim={
          !allowDeleteColumn({columnIndex: j, canAddRemoveColumns, getSelectionRange, table})
        }
        iconPrefix={hideColumnHeaderIcons ? null : getIconPrefix(fileId, table, columnConstraint)}
        isLeftAlign={isLeftAlignColumnHeader}
        columnIsOutdated={_columnIsOutdated(table, j)}
        isHeaderCell={true}
        dataTestAttrs={{component: 'TableHeaderRow', element: 'columnLabelCell'}}
        isConfig={isConfig}
        isProtected={isProtected}
        finalizeAndExitEditMode={finalizeAndExitEditMode}
        freezeThroughColumn={freezeThroughColumn}
        unfreezeFromColumn={unfreezeFromColumn}
        {...tableCellAffixedProps}
        {...getSearchResultPropsForIndex({searchResultsTree, selectedSearchResultTree}, j)}
      />
    );
  }

  function renderLeadingCell(): JSX.Element | string {
    if (table._rowHeader == null) {
      return (
        <td
          key="left-col-filter"
          className={classNames(
            'mediocre-tableEditable-axisCell mediocre-tableEditable-axisCell--header cell-sticky',
            {
              'align-left': isLeftAlignRowHeader,
              'cell-rightShadow': showAffixedShadow && affixedColumnCount === 0,
            }
          )}
        />
      );
    }
    return table._rowHeader;
  }

  const rowStyle = rowHeight != null ? {height: rowHeight} : {};
  const {highlightCustomColumns} = EditorPropertiesContext.useContext();

  return (
    <tr
      className={classNames(
        {'mediocre-tableEditable-headerRow--sticky': stickyHeaderCells},
        {'mediocre-tableEditable-headerRow--noAxis': firstColumnWidth === 0}
      )}
      style={{
        ...rowStyle,
        top: stickyHeaderCells ? stickyTop : undefined,
      }}
    >
      {TableUtil.mapColumns({
        sliceStart,
        sliceEnd,

        ghostCellsCount: ghostCellsCount ?? 0,
        normalCellsCount: table.table[0].length,

        mapLeadingCell: () => renderLeadingCell(),
        mapNormalCell: (j: number) => renderCell(j, highlightCustomColumns || false),
        mapGhostCell: (j: number) => renderCell(j, highlightCustomColumns || false),
      })}
      {stickyHeaderCells && (
        // When the header row is sticky, the top cells will lose their borders due to how tables
        // collapse borders. These are stand-ins for those.
        <>
          <td className="mediocre-tableEditable-headerRow--sticky-borderTop" />
          <td className="mediocre-tableEditable-headerRow--sticky-borderBottom" />
        </>
      )}
    </tr>
  );
}

/**
 * Determines if a column can be inserted in the specified location.
 *
 * If ColumnConstraint.isColumnUserConfigurable is present on any selected column, this will override
 * the deprecated TableUtil.legacyFirstEditableColumnIndex(table) behavior.
 *
 * This is shared between the regular column header row and Single Inputs so that the logic for
 * whether the "Insert column/single input left/right" option is enabled stays identical.
 */
export function canInsertColumns({
  table,
  canAddRemoveColumns,
  insertableColumnInfos,
  getSelectionRange,
  columnIndex,
  direction,
}: {
  table: Table;
  canAddRemoveColumns: boolean;
  insertableColumnInfos: Array<InsertableColumnInfo> | undefined;
  getSelectionRange: (dim: TableDimension, j: number) => SelectionRange | null;
  columnIndex: number;
  direction: DIRECTION;
}): boolean {
  // short circuit checks on table config
  if (
    !canAddRemoveColumns ||
    (TableUtil.disallowCustomColumns(table) && (insertableColumnInfos?.length ?? 0) === 0)
  ) {
    return false;
  }

  const range = getSelectionRange(TABLE_COLUMN, columnIndex);
  if (range == null) {
    return false;
  }

  // check for the presence of an isColumnUserConfigurable method
  for (let i = range.minIndex; i <= range.maxIndex; i++) {
    const columnConstraint = table.columnConstraints?.[i];
    if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
      return true;
    }
  }

  // Fall back to deprecated firstEditableColumnIndex behavior
  const firstEditableIndex = TableUtil.legacyFirstEditableColumnIndex(table);
  if (firstEditableIndex != null) {
    const newIndex = direction === DIRECTION.BEFORE ? range.minIndex : range.maxIndex + 1;
    if (newIndex < firstEditableIndex) {
      return false;
    }
  }

  return true;
}

/**
 * Returns column mapping icon if this is a destination column, otherwise the column type icon.
 */
export function getIconPrefix(
  fileId: string,
  table: Table,
  columnConstraint: ColumnConstraint<StructuredTableColumn> | null
): BenchlingIconName | null {
  if (hasDestinationColumnMapping(fileId, table, columnConstraint)) {
    return 'column-mapping';
  }
  if (columnConstraint?.columnDefinition) {
    return columnDefinitionToIcon(columnConstraint.columnDefinition);
  }
  return null;
}

export function getCellComponent(table: Table, j: number): typeof TableAxisCell | typeof UnitsTableAxisCell {
  switch (TableUtil.getColumnDefinitionType(table, j)) {
    case TableColumnType.QUANTITY:
    case TableColumnType.CONCENTRATION:
      return UnitsTableAxisCell;
    default:
      return TableAxisCell;
  }
}

/**
 * Returns true if the given column is a destination column for at least one table mapping
 */
function hasDestinationColumnMapping(
  fileId: string,
  table: Table,
  columnConstraint: ColumnConstraint<StructuredTableColumn> | null
): boolean {
  const tableId = table.provenanceUuid;
  const columnId = columnConstraint?.structuredTableColumn?.provenanceUuid;

  if (tableId == null || columnId == null) {
    return false;
  }

  const structuredTableMappingsState = StructuredTableMappingsGlobalState.getState(fileId);
  const mappingsForColumn = StructuredTableMappingsStateManager.getMappingsForDestinationColumn(
    structuredTableMappingsState,
    tableId,
    columnId
  );
  return Boolean(mappingsForColumn.length);
}

export default React.memo(
  TableHeaderRow,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    // TODO(BNCH-113857): Switch back to shallow equal once redux is enabled
    Object.keys(nextProps).every((key) => isEqual(prevProps[key], nextProps[key]))
);
